import { type RelativeSlot, type RelativeSlotKey, relativeSlotKeyForCode } from '@liza/fhir-utils';
import { formatCodeableConcept } from '@medplum/core';
import type { Age, CodeableConcept, FamilyMemberHistory, Period, Quantity, Range } from '@medplum/fhirtypes';
import { format, parseISO } from 'date-fns';
import type { TFunction } from 'i18next';
import { DATE_FORMATS } from '../../utils/date';

/** Translated name of every canonical relative. Each key is a literal `t` call so extraction keeps
 * both catalogs complete. */
export const relativeSlotLabels = (t: TFunction<'records'>): Record<RelativeSlotKey, string> => ({
  mother: t('relativeSlot.mother'),
  father: t('relativeSlot.father'),
  parent: t('relativeSlot.parent'),
  stepmother: t('relativeSlot.stepmother'),
  stepfather: t('relativeSlot.stepfather'),
  stepparent: t('relativeSlot.stepparent'),
  'adoptive-mother': t('relativeSlot.adoptive-mother'),
  'adoptive-father': t('relativeSlot.adoptive-father'),
  'adoptive-parent': t('relativeSlot.adoptive-parent'),
  'foster-mother': t('relativeSlot.foster-mother'),
  'foster-father': t('relativeSlot.foster-father'),
  'foster-parent': t('relativeSlot.foster-parent'),
  sister: t('relativeSlot.sister'),
  brother: t('relativeSlot.brother'),
  sibling: t('relativeSlot.sibling'),
  'twin-sister': t('relativeSlot.twin-sister'),
  'twin-brother': t('relativeSlot.twin-brother'),
  twin: t('relativeSlot.twin'),
  'half-sister': t('relativeSlot.half-sister'),
  'half-brother': t('relativeSlot.half-brother'),
  'half-sibling': t('relativeSlot.half-sibling'),
  stepsister: t('relativeSlot.stepsister'),
  stepbrother: t('relativeSlot.stepbrother'),
  stepsibling: t('relativeSlot.stepsibling'),
  daughter: t('relativeSlot.daughter'),
  son: t('relativeSlot.son'),
  child: t('relativeSlot.child'),
  stepdaughter: t('relativeSlot.stepdaughter'),
  stepson: t('relativeSlot.stepson'),
  stepchild: t('relativeSlot.stepchild'),
  'adopted-daughter': t('relativeSlot.adopted-daughter'),
  'adopted-son': t('relativeSlot.adopted-son'),
  'adopted-child': t('relativeSlot.adopted-child'),
  'foster-daughter': t('relativeSlot.foster-daughter'),
  'foster-son': t('relativeSlot.foster-son'),
  'foster-child': t('relativeSlot.foster-child'),
  'maternal-grandmother': t('relativeSlot.maternal-grandmother'),
  'maternal-grandfather': t('relativeSlot.maternal-grandfather'),
  'maternal-grandparent': t('relativeSlot.maternal-grandparent'),
  'paternal-grandmother': t('relativeSlot.paternal-grandmother'),
  'paternal-grandfather': t('relativeSlot.paternal-grandfather'),
  'paternal-grandparent': t('relativeSlot.paternal-grandparent'),
  grandmother: t('relativeSlot.grandmother'),
  grandfather: t('relativeSlot.grandfather'),
  grandparent: t('relativeSlot.grandparent'),
  'great-grandmother': t('relativeSlot.great-grandmother'),
  'great-grandfather': t('relativeSlot.great-grandfather'),
  'great-grandparent': t('relativeSlot.great-grandparent'),
  'maternal-aunt': t('relativeSlot.maternal-aunt'),
  'maternal-uncle': t('relativeSlot.maternal-uncle'),
  'paternal-aunt': t('relativeSlot.paternal-aunt'),
  'paternal-uncle': t('relativeSlot.paternal-uncle'),
  aunt: t('relativeSlot.aunt'),
  uncle: t('relativeSlot.uncle'),
  'maternal-cousin': t('relativeSlot.maternal-cousin'),
  'paternal-cousin': t('relativeSlot.paternal-cousin'),
  cousin: t('relativeSlot.cousin'),
  niece: t('relativeSlot.niece'),
  nephew: t('relativeSlot.nephew'),
  'niece-nephew': t('relativeSlot.niece-nephew'),
  granddaughter: t('relativeSlot.granddaughter'),
  grandson: t('relativeSlot.grandson'),
  grandchild: t('relativeSlot.grandchild'),
  spouse: t('relativeSlot.spouse'),
  other: t('relativeSlot.other'),
  unspecified: t('relativeSlot.unspecified'),
});

/** The name a canonical relative renders under: the translated slot, or the source text for a
 * relation the tables do not know. */
export const relativeLabel = (slot: RelativeSlot, t: TFunction<'records'>): string =>
  slot.key === 'other' && slot.text ? slot.text : relativeSlotLabels(t)[slot.key];

/** Every condition on one resource as the source wrote it, for a row under a merged relative. */
export const formatRecordConditions = (history: FamilyMemberHistory, t: TFunction<'records'>): string | undefined => {
  const labels = (history.condition ?? []).map((c) => formatCodeableConcept(c.code) || t('unknownCondition'));
  return labels.length > 0 ? labels.join(', ') : undefined;
};

/** The relationship as the source recorded it, for a resource row under a merged relative. */
export const formatRelationship = (relationship: CodeableConcept | undefined, t: TFunction<'records'>): string => {
  if (!relationship) return t('unknownRelationship');
  if (relationship.text) return relationship.text;
  const coding = relationship.coding?.find((c) => c.display || c.code);
  if (coding?.display) return coding.display;
  if (coding?.code) {
    const slotKey = relativeSlotKeyForCode(coding.code);
    return slotKey ? relativeSlotLabels(t)[slotKey] : coding.code;
  }
  return t('unknownRelationship');
};

const isAgeOrQuantity = (value: unknown): value is Quantity =>
  typeof value === 'object' && value !== null && 'value' in value && typeof (value as Quantity).value === 'number';

const isRange = (value: unknown): value is Range =>
  typeof value === 'object' && value !== null && ('low' in value || 'high' in value);

const formatAgeUnit = (q: Age | Quantity, t: TFunction<'records'>): string => {
  if (q.value === undefined) return '';
  switch (q.code ?? q.unit) {
    case 'a':
    case 'years':
    case 'year':
      return t('ageYears', { count: q.value });
    case 'mo':
    case 'months':
    case 'month':
      return t('ageMonths', { count: q.value });
    case 'wk':
    case 'weeks':
    case 'week':
      return t('ageWeeks', { count: q.value });
    case 'd':
    case 'days':
    case 'day':
      return t('ageDays', { count: q.value });
    default:
      return q.unit ? `${q.value} ${q.unit}` : String(q.value);
  }
};

type AgeLike = Age | Range | string | undefined;

export const formatAge = (age: AgeLike, t: TFunction<'records'>): string | undefined => {
  if (age === undefined) return undefined;
  if (typeof age === 'string') return age;
  if (isRange(age)) {
    const low = age.low ? formatAgeUnit(age.low, t) : undefined;
    const high = age.high ? formatAgeUnit(age.high, t) : undefined;
    if (low && high) return `${low}–${high}`;
    return low ?? high;
  }
  if (isAgeOrQuantity(age)) return formatAgeUnit(age, t);
  return undefined;
};

const formatPeriod = (period: Period): string | undefined => {
  const start = period.start ? format(parseISO(period.start), DATE_FORMATS.ISO_DATE) : undefined;
  const end = period.end ? format(parseISO(period.end), DATE_FORMATS.ISO_DATE) : undefined;
  if (start && end) return `${start} – ${end}`;
  return start ?? end;
};

export const formatBorn = (history: FamilyMemberHistory): string | undefined => {
  if (history.bornDate) return format(parseISO(history.bornDate), DATE_FORMATS.ISO_DATE);
  if (history.bornString) return history.bornString;
  if (history.bornPeriod) return formatPeriod(history.bornPeriod);
  return undefined;
};

export const formatDeceased = (history: FamilyMemberHistory, t: TFunction<'records'>): string | undefined => {
  if (history.deceasedBoolean === true) return t('deceased');
  if (history.deceasedBoolean === false) return undefined;
  if (history.deceasedDate)
    return t('deceasedOn', { date: format(parseISO(history.deceasedDate), DATE_FORMATS.ISO_DATE) });
  if (history.deceasedString) return t('deceasedWithDetail', { detail: history.deceasedString });
  if (history.deceasedAge) {
    const age = formatAgeUnit(history.deceasedAge, t);
    return age ? t('deceasedAtAge', { age }) : t('deceased');
  }
  if (history.deceasedRange) {
    const age = formatAge(history.deceasedRange, t);
    return age ? t('deceasedAtAge', { age }) : t('deceased');
  }
  return undefined;
};
